import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Modal } from '../ui/Modal';
import { Tag } from '../../types/api';
import { useUpdateTagName } from '../../services/tagService';
import clsx from 'clsx';

interface TagEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  tag: Tag;
}

function TagEditModal({ isOpen, onClose, tag }: TagEditModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(tag.name);
  const [error, setError] = useState<string | null>(null);

  const updateTagMutation = useUpdateTagName();

  // reset form when modal opens/closes or tag changes
  useEffect(() => {
    if (isOpen) {
      setName(tag.name);
      setError(null);
    }
  }, [isOpen, tag]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t`Tag name is required`);
      return;
    }

    if (name.trim().length > 250) {
      setError(t`Tag name must be 250 characters or less`);
      return;
    }

    // Don't update if name hasn't changed
    if (name.trim() === tag.name.trim()) {
      onClose();
      return;
    }

    try {
      const updatedTag = await updateTagMutation.mutateAsync({
        tagId: tag.id,
        newName: name.trim(),
      });

      // If we're on the tag page, redirect to the new slug
      if (location.pathname.startsWith('/tags/') && updatedTag?.slug) {
        navigate({
          to: '/tags/$slug',
          params: { slug: updatedTag.slug },
        });
      }

      onClose();
    } catch (err) {
      setError((err as Error).message || t`Sorry, there was an error. Please try again.`);
    }
  };

  const isPending = updateTagMutation.isPending;

  return (
    <Modal.Root
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={t`Edit tag`}
      initialFocusRef={nameInputRef}
      className="w-full max-w-md"
    >
      <Modal.Header title={t`Edit Tag`} />
      <Modal.Body>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="tag-name" className="form-label">
              <Trans>Name</Trans>
            </label>
            <input
              ref={nameInputRef}
              id="tag-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t`Tag name`}
              maxLength={250}
              className={clsx('form-input', error && !name.trim() && 'form-input-error')}
              disabled={isPending}
            />
          </div>

          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Actions>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isPending}
            className={clsx('button-primary')}
          >
            {updateTagMutation.isPending ? (
              <Trans>Saving...</Trans>
            ) : (
              <Trans>Save Changes</Trans>
            )}
          </button>
        </Modal.Actions>
      </Modal.Footer>
    </Modal.Root>
  );
}

export default TagEditModal;
